import type { ReactNode } from 'react';

/**
 * The top of every screen, decided once.
 *
 * The heading size was already consistent — 26px everywhere — but nothing else
 * was. Some pages put the title alone and followed it with `mb-6`; others sat
 * it in a flex row beside controls and dropped the margin; the description
 * capped at 620px on some screens and 660px on others. Read one after another
 * they looked like three designs, because structurally they were.
 *
 * `actions` is why the variation existed in the first place: Planning, Month
 * end and Completed all need controls level with the title. Expressing that as
 * a prop means they get it without each inventing its own arrangement — and
 * the spacing below the block stays identical whether or not it is used, which
 * is the part the eye actually notices when moving between screens.
 *
 * The description cap is deliberate and unrelated to the page width. Measure,
 * not proportion, governs readability: a line stays comfortable at roughly 75
 * characters however wide the container around it grows.
 */

export interface PageHeaderProps {
  readonly title: string;
  readonly description?: ReactNode;
  /** Controls sitting level with the title — filters, toggles, a month picker. */
  readonly actions?: ReactNode;
}

export function PageHeader({ title, description, actions }: PageHeaderProps) {
  return (
    <header className="mb-6">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <h1 className="fs-26 leading-tight font-bold tracking-[-0.02em] text-ink">{title}</h1>
        {actions !== undefined && (
          <div className="flex flex-none flex-wrap items-center gap-2">{actions}</div>
        )}
      </div>

      {description !== undefined && <p className="mt-1.5 max-w-[40rem] fs-13 text-muted">{description}</p>}
    </header>
  );
}
